// Single source of truth for how a scheduled visit is bucketed across the portals — patient Visits
// and doctor Schedule both import this, so the surfaces can't drift in what counts as upcoming vs
// past. Status is authoritative once the API worker sweeps past-unstarted visits to `no_show`; the
// time check only disambiguates a scheduled/confirmed row that hasn't been swept yet, using the same
// start+duration threshold the server uses, so client and server agree.
export type VisitBucket = "live" | "upcoming" | "completed" | "cancelled" | "missed";

export interface VisitLike {
  status: string;
  scheduled_at: string;
  duration_minutes?: number | null;
}

export function classifyVisit(v: VisitLike, now: number = Date.now()): VisitBucket {
  switch (v.status) {
    case "cancelled":
      return "cancelled";
    case "completed":
      return "completed";
    case "no_show":
      return "missed";
    case "live":
      return "live";
    default: {
      // scheduled / confirmed: upcoming until the whole slot (start + duration) has elapsed, after
      // which it's a no-show the server just hasn't swept yet.
      const endish = new Date(v.scheduled_at).getTime() + (v.duration_minutes ?? 30) * 60_000;
      return endish > now ? "upcoming" : "missed";
    }
  }
}

/** Belongs in the "upcoming" list and is join-relevant (future visit or one happening right now). */
export function isUpcomingVisit(v: VisitLike, now?: number): boolean {
  const b = classifyVisit(v, now);
  return b === "upcoming" || b === "live";
}
